import { useEffect, useRef, useState } from 'react';
import { useAddTopFolder, type AddableFolder } from '../api/uploads.ts';
import { BAR_ACTION } from './barAction.ts';

/**
 * The Documents "+" (145; Arthur, 22 October): on the right of the bar, opposite Back, at the top level only, shown
 * only when there is something to add. It opens "Add a folder", a stack of big buttons in the style of the
 * communications "Other..." list, one for each catalogue folder offered. Opens on click or tap, and from the keyboard
 * (Enter, Space or the down arrow); closes on Escape (focus returns to it), on a click elsewhere, when focus leaves
 * it, or once a folder is added. The arrow keys, Home, End and Tab move through the stack.
 */
export function AddTopFolder({ clientId, offered }: { clientId: string; offered: AddableFolder[] }) {
  const add = useAddTopFolder(clientId);
  const [open, setOpen] = useState(false);
  // Fixed below the trigger, its right edge on the trigger's: the panel clips anything absolutely placed past its edge
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = trigger.current?.getBoundingClientRect();
      if (r) setAt({ top: r.bottom, right: Math.max(8, document.documentElement.clientWidth - r.right) });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const away = (e: PointerEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', away);
    return () => {
      document.removeEventListener('pointerdown', away);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const focusItem = (i: number) => {
    const n = offered.length;
    items.current[((i % n) + n) % n]?.focus();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    const here = items.current.findIndex((el) => el === document.activeElement);
    // Stopped here: the toolbar's menu (Toolbar.tsx) answers every Escape on the document by focusing its own button,
    // open or not, and would take focus away from "+".
    if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); trigger.current?.focus(); return; }
    // open already: focus at once; opening: once the stack is drawn
    const go = (i: number) => { if (open) focusItem(i); else { setOpen(true); setTimeout(() => focusItem(i), 0); } };
    if (e.key === 'ArrowDown') { e.preventDefault(); go(here + 1); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); go(here < 0 ? -1 : here - 1); return; }
    if (e.key === 'Home' && here >= 0) { e.preventDefault(); focusItem(0); return; }
    if (e.key === 'End' && here >= 0) { e.preventDefault(); focusItem(-1); }
  };

  return (
    <div ref={wrap} className="relative" onKeyDown={onKeyDown}
         onBlur={(e) => { if (!wrap.current?.contains(e.relatedTarget as Node | null)) setOpen(false); }}>
      <button ref={trigger} type="button" aria-haspopup="true" aria-expanded={open} aria-label="Add a folder" title="Add a folder"
              onClick={() => { add.reset(); setOpen((o) => !o); }}
              className={`${BAR_ACTION} min-w-[44px] text-2xl leading-none`}>
        <span aria-hidden="true">+</span>
      </button>
      {open && at && (
        // pt-2 rather than a margin: the padding belongs to the menu, so the pointer moving onto the stack never leaves it
        <div className="fixed z-30 pt-2" style={{ top: at.top, right: at.right }}>
          <div className="flex min-w-[12rem] flex-col gap-2 rounded-lg border border-neutral-300 bg-panel p-2 shadow-lg">
            <p id="add-top-folder" className="px-1 text-sm font-semibold text-neutral-700">Add a folder</p>
            <ul aria-labelledby="add-top-folder" className="flex flex-col gap-2">
              {offered.map((f, i) => (
                <li key={f.code}>
                  <button ref={(el) => { items.current[i] = el; }} type="button" disabled={add.isPending}
                          className={`${bigButton} w-full text-left`}
                          onClick={() => add.mutate(f.code, { onSuccess: () => { setOpen(false); trigger.current?.focus(); } })}>
                    {f.name}
                  </button>
                </li>
              ))}
            </ul>
            {add.error && <p role="alert" className="max-w-[16rem] px-1 text-sm text-warning-fg">{(add.error as Error).message}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

const bigButton = 'min-h-[56px] rounded-lg bg-brand px-6 text-lg font-semibold text-on-brand';
